<template>
  <div>
    <!-- Top navbar -->
    <b-modal no-close-on-backdrop no-close-on-esc centered size="mg" hide-footer ref="closeSessionModal"
      id="closeSessionModal" hide-header :hide-header-close="true">
      <div>
        <p>Do you want to close the session?</p>
        <p><b>The session will be closed and all changes will be committed.</b></p>
        <div>
          <b-btn variant="primary"
            @click="closedSession(currentSession)">Yes</b-btn>
          <b-btn variant="secondary" @click="$refs.closeSessionModal.hide()">No</b-btn>
        </div>
      </div>
    </b-modal>
    <b-modal no-close-on-backdrop no-close-on-esc centered size="mg" hide-footer ref="discardSessionModal"
      id="discardSessionModal" hide-header :hide-header-close="true">
      <div>
        <p>Do you want to discard the session?</p>
        <p><b>The session will be discarded and all changes will be lost.</b></p>
        <div>
          <b-btn variant="primary" @click="discardSession(currentSession)">Yes</b-btn>
          <b-btn variant="secondary" @click="$refs.discardSessionModal.hide()">No</b-btn>
        </div>
      </div>
    </b-modal>
    <b-modal no-close-on-backdrop no-close-on-esc centered size="mg" hide-footer ref="outlierSessionModal"
      id="outlierSessionModal" hide-header :hide-header-close="true">
      <div>
        <b-form-group label="Please enter a percentage" label-for="outlier">
          <b-form-input type="number" min="0" max="100" @change="checkOutlierPercentage"
            v-model.number="currentSession.outlier" placeholder="%"></b-form-input>
        </b-form-group>
        <div class="float-right">
          <b-btn variant="primary" @click="outlierSession(form)">OK</b-btn>
          <b-btn variant="warning" @click="$refs.outlierSessionModal.hide()">Cancel</b-btn>
        </div>
      </div>
    </b-modal>
    <b-modal no-close-on-backdrop no-close-on-esc centered size="sm" hide-footer ref="ignoreWarningsModal"
      id="ignoreWarningsModal" hide-header :hide-header-close="true">
      <div>
        <p class="mb-2"><strong>Ignore {{ suspiciousLots.length }} warnings?</strong></p>
        <p class="mb-2">These readings will be excluded from validation checks.</p>
        <p class="mb-3">This action will be logged.</p>
        <div class="d-flex justify-content-end">
          <b-btn variant="secondary" class="mr-2" @click="cancelIgnoreWarnings">Cancel</b-btn>
          <b-btn variant="danger" @click="confirmIgnoreWarnings">Ignore &amp; Continue</b-btn>
        </div>
      </div>
    </b-modal>
    <b-modal no-close-on-backdrop no-close-on-esc centered size="mg" hide-footer ref="closeSessionElectricModal"
      id="closeSessionElectricModal" hide-header :hide-header-close="true">
      <div>
        <b-form-group :label="'Total ' + electricityUnitLabel + ' for the month'" label-for="unitConsumed">
          <b-form-input type="number" v-model="currentSession.units_consumed_by_building"
            placeholder="Total Consumption (kwH)"></b-form-input>
        </b-form-group>
        <b-form-group label="Total load charges to be allocated" label-for="loadConsumed">
          <b-form-input type="number" v-model="currentSession.load_consumed_by_building"
            :placeholder="'Enter 6 month demand high (' + currencySymbol + ')'"></b-form-input>
        </b-form-group>
        <div class="float-right">
          <b-btn variant="primary"
            @click="$refs.closeSessionElectricModal.hide(), $refs.closeSessionModal.show()">Save</b-btn>
          <b-btn variant="warning"
            @click="$refs.closeSessionElectricModal.hide(), $refs.closeSessionModal.show()">Skip</b-btn>
          <b-btn variant="secondary"
            @click="$refs.closeSessionElectricModal.hide(), currentSession.units_consumed_by_building = '', currentSession.load_consumed_by_building = ''">Cancel</b-btn>
        </div>
      </div>
    </b-modal>
    <b-modal no-close-on-backdrop no-close-on-esc centered size="mg" hide-footer ref="closeSessionWaterModal"
      id="closeSessionWaterModal" hide-header :hide-header-close="true">
      <div>
        <p class="font-weight-bold mb-1">If water was supplied by a bowser or tanker this month, you can allocate the cost to each lot.</p>
        <p class="text-muted small mb-3">Enter both fields, or skip if no tanker was used this month.</p>
        <b-form-group :label="'Tanker Cost (' + currencySymbol + ')'" label-for="tankerCost">
          <b-form-input
            id="tankerCost"
            type="number"
            min="0"
            step="0.01"
            v-model="currentSession.tanker_cost"
            placeholder="e.g. 4500.00">
          </b-form-input>
        </b-form-group>
        <b-form-group :label="'Tanker Volume (' + waterUnitLabel + ')'" label-for="tankerVolume">
          <b-form-input
            id="tankerVolume"
            type="number"
            min="0"
            step="0.01"
            v-model="currentSession.tanker_volume"
            placeholder="e.g. 18.5">
          </b-form-input>
        </b-form-group>
        <div class="float-right">
          <b-btn variant="primary" class="mr-1" @click="saveWaterDetails">Save</b-btn>
          <b-btn variant="warning" class="mr-1" @click="skipWaterDetails">Skip</b-btn>
          <b-btn variant="secondary" @click="cancelWaterDetails">Cancel</b-btn>
        </div>
      </div>
    </b-modal>
    <b-modal no-close-on-backdrop no-close-on-esc centered size="mg" hide-footer ref="autoCompleteLotsModal"
      id="autoCompleteLotsModal" hide-header :hide-header-close="true">
      <div>
        <p>This action will complete missing lots with zero consumption. Continue?</p>
        <div>
          <b-btn variant="primary" @click="autoCompleteMissingLots(session)">Yes</b-btn>
          <b-btn variant="secondary" @click="$refs.autoCompleteLotsModal.hide()">No</b-btn>
        </div>
      </div>
    </b-modal>

    <!-- Post-close success modal with actions -->
    <b-modal no-close-on-backdrop no-close-on-esc centered size="lg" hide-footer ref="postCloseModal"
      id="postCloseModal" hide-header :hide-header-close="true">
      <div class="text-center">
        <div class="mb-3">
          <i class="fas fa-check-circle text-success" style="font-size: 48px;"></i>
        </div>
        <h3 class="mb-2">Session Closed Successfully</h3>
        <p class="text-muted mb-4" v-if="mostRecentClosedSession">
          {{ mostRecentClosedSession.site ? mostRecentClosedSession.site.name : '' }} - {{ mostRecentClosedSession.service_type.service_name }}
        </p>

        <div class="d-flex justify-content-center mt-3 mb-2">
          <b-btn variant="primary" @click="actionViewReadings" class="mx-2">
            <i class="far fa-eye mr-2"></i> View Readings
          </b-btn>

          <b-dropdown variant="info" text="Download" class="mx-2">
            <template #button-content><i class="fas fa-download mr-2"></i>Download</template>
            <b-dropdown-item @click="actionDownloadExcel">
              <i class="fas fa-file-excel mr-2"></i> Microsoft Excel
            </b-dropdown-item>
            <b-dropdown-item @click="actionDownloadPDF">
              <i class="fas fa-file-pdf mr-2"></i> Bills (PDF)
            </b-dropdown-item>
            <b-dropdown-item @click="actionDownloadNotify">
              <i class="fas fa-file-pdf mr-2"></i> Notice of Use (PDF)
            </b-dropdown-item>
            <b-dropdown-item @click="actionDownloadText">
              <i class="fas fa-file-alt mr-2"></i> Text File
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown variant="success" class="mx-2">
            <template #button-content><i class="fas fa-envelope mr-2"></i>Email</template>
            <b-dropdown-item @click="actionEmailBill">
              <i class="fas fa-envelope mr-2"></i> Bill
            </b-dropdown-item>
            <b-dropdown-item @click="actionEmailNotice">
              <i class="fas fa-envelope mr-2"></i> Notice of Consumption
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <div class="text-center mt-3 mb-2">
          <b-btn variant="link" class="text-muted" @click="dismissPostCloseModal">
            <i class="fas fa-times mr-1"></i> Close
          </b-btn>
        </div>
      </div>
    </b-modal>

    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <!-- Brand -->
        <router-link :to="{ name: 'openSession' }" class="h4 mb-0 text-uppercase d-none d-lg-inline-block">Open
          Session</router-link>
        <user-data></user-data>
      </div>
    </nav>
    <!-- Page content -->
    <div class="container-fluid mt-0 pt-5 pt-md-8">
      <div class="session-summary-bar shadow-sm summary-box mb-4">
        <!-- Session selector always visible -->
        <div class="stat-chip stat-chip--selector">
          <span class="stat-label">Session Name</span>
          <div class="d-flex align-items-center">
            <b-form-select id="session" required @change="selectedSession(session)" v-model="session"
              class="form-control-alternative form-control-sm">
              <option value="null" class="bg-grey text-white" disabled selected>Please select session</option>
              <option value="0">Add New Session</option>
              <optgroup label="Open Session" v-if="openSessions.length > 0">
                <option v-for="option in openSessions" :key="option.id"
                  v-bind:value="option.site_id + '-' + option.service_type_id + '-' + option.total_count + '-' + option.completed_count">
                  {{ option.text }}</option>
              </optgroup>
              <optgroup label="Completed Session" v-if="completedSessions.length > 0">
                <option v-for="option in completedSessions" :key="option.id"
                  v-bind:value="option.site_id + '-' + option.service_type_id + '-' + option.total_count + '-' + option.completed_count">
                  {{ option.text }}</option>
              </optgroup>
            </b-form-select>
            <div @click="refreshSessions()" class="ml-2 c-pointer text-color-brand-green">
              <i class="fas fa-sync" :class="{ 'fa-spin': refreshing }"></i>
            </div>
          </div>
        </div>

        <!-- Session stats — visible when a session is selected -->
        <template v-if="session != 0 && session != null && survoyor !== null">
          <div class="stat-chip" v-if="currentSiteName">
            <span class="stat-label">Site</span>
            <span class="stat-value">{{ currentSiteName }}</span>
          </div>
          <div class="stat-chip" v-if="currentServiceTypeName">
            <span class="stat-label">Type</span>
            <span class="stat-value">{{ currentServiceTypeName }}</span>
          </div>
          <div class="stat-chip">
            <span class="stat-label">Surveyor</span>
            <span class="stat-value">{{ survoyor.email }}</span>
          </div>
          <div class="stat-chip">
            <span class="stat-label">Started On</span>
            <span class="stat-value">{{ selectedSessionFirstRow && selectedSessionFirstRow.survey_start_date ? $options.filters.fmtDateTime(selectedSessionFirstRow.survey_start_date) : '-' }}</span>
          </div>
          <div class="stat-chip">
            <span class="stat-label">Previous Billable Units</span>
            <span class="stat-value">{{ totalBillableUnits.previous | formatNumber }} <small class="text-muted font-weight-normal">{{ currentSessionUnitLabel }}</small></span>
          </div>
          <div class="stat-chip">
            <span class="stat-label">Current Billable Units</span>
            <span class="stat-value">
              {{ totalBillableUnits.current | formatNumber }} <small class="text-muted font-weight-normal">{{ currentSessionUnitLabel }}</small>
              <i :class="totalBillableUnits.current > totalBillableUnits.previous ? 'fa fa-arrow-up text-danger ml-1' : 'fa fa-arrow-down text-danger ml-1'"></i>
            </span>
          </div>
        </template>

        <!-- Actions -->
        <div class="stat-chip stat-chip--actions ml-auto" v-if="session !== '0'">
          <template v-if="session != 0 && session != null && !viewDetailsSession">
            <b-button variant="primary" size="sm" @click="showOutlier()"><i class='fa fa-search mr-1'></i>Analyse</b-button>
            <b-button variant="success" size="sm" class="mx-1"
              :disabled="completedCount !== totalCount || (suspiciousLots.length > 0 && !allReviewed)"
              :class="{ 'cursor-disabled': completedCount !== totalCount || (suspiciousLots.length > 0 && !allReviewed) }"
              @click="checkElectricity"><i class="fa fa-check mr-1"></i>Close</b-button>
            <b-button variant="danger" size="sm" @click="$refs.discardSessionModal.show()"><i class='fa fa-trash mr-1'></i>Discard</b-button>
            <span class="mx-2" style="border-left: 1px solid #dbeafe; align-self: stretch;"></span>
          </template>
          <b-button variant="outline-primary" size="sm" @click="session = '0'">
            <i class="fas fa-plus mr-1"></i> Create Session
          </b-button>
        </div>
      </div>

      <b-card no-body class="my-4" v-if="suspiciousLots.length > 0 && session != 0 && session != null">
        <b-card-header :class="warningsAcknowledged ? 'bg-success text-white' : 'bg-danger text-white'" class="d-flex justify-content-between align-items-center"
          @click="showSuspicionCollapse = !showSuspicionCollapse" style="cursor: pointer" role="button">
          <div>
            <h4 v-if="!warningsAcknowledged" class="mb-0">⚠️ Review Required ({{ suspiciousLots.length }})</h4>
            <h4 v-else class="mb-0 text-white">✓ Warnings Acknowledged</h4>
          </div>
          <!-- <div class="d-flex align-items-center" @click.stop>
            <span :class="warningsAcknowledged ? 'text-dark' : 'text-white'">Ignore all warnings</span>
            <b-form-checkbox switch class="ml-2" v-model="ignoreWarnings" @change="onIgnoreWarningsToggle"></b-form-checkbox>
          </div> -->
          <i :class="showSuspicionCollapse ? 'fa fa-minus' : 'fa fa-plus'"></i>
        </b-card-header>

        <b-collapse v-model="showSuspicionCollapse">
          <b-card-body>
            <div v-if="!warningsAcknowledged">
              <p class="font-weight-bold mb-3">Please review the following before closing:</p>
              <ul class="mb-4">
                <li v-for="(message, index) in suspiciousLots" :key="index" class="mb-2">
                  {{ message }}
                </li>
              </ul>
              <b-button variant="danger" @click="acknowledgeWarnings" class="mb-3">
                <i class="fas fa-check mr-2"></i> I've Reviewed These Warnings - Acknowledge & Continue
              </b-button>
            </div>
            <div v-else class="alert alert-info text-dark mb-0">
              <i class="fas fa-check-circle mr-2"></i>You've acknowledged {{ suspiciousLots.length }} warning(s). You can now close the session.
            </div>
          </b-card-body>
        </b-collapse>
      </b-card>

      <b-card no-body class="my-4" v-if="incompleteData.length && session != 0 && session != null">
        <b-card-header class="bg-danger text-white d-flex justify-content-between align-items-center"
          @click="showCollapse = !showCollapse" style="cursor: pointer" role="button">
          <h4 class="mb-0">⚠️ Missing Readings ({{ incompleteData.length }})</h4>
          <i :class="showCollapse ? 'fa fa-minus' : 'fa fa-plus'"></i>
        </b-card-header>

        <b-collapse v-model="showCollapse">
          <b-card-body>
            <div class="row align-items-start">
              <div class="col">
                <div class="table-responsive">
                  <table class="table table-sm table-bordered mb-0 missing-lots-table">
                    <thead class="thead-light">
                      <tr>
                        <th class="text-center">Lot No</th>
                        <th class="text-center">Barcode</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr :key="`${item.lot_no}-${index}`" v-for="(item, index) in incompleteData">
                        <td class="text-center font-weight-bold align-middle">{{ item.lot_no }}</td>
                        <td class="text-center align-middle">
                          <span class="badge badge-pill badge-primary barcode-pill">
                            {{ item.barcode || 'Not available' }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="col-auto">
                <b-button variant="info" @click="autoCompleteLotsModal()">Complete</b-button>
              </div>
            </div>
          </b-card-body>
        </b-collapse>
      </b-card>


      <div class="row my-4" v-if="session != 0 && session != null && !viewDetailsSession">
        <div class="col-md-12 mb-5 mb-xl-0">
          <div class="card shadow">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">
                    <span v-if="isOutlierActive">Readings - ({{ outlierSessionData.data ? outlierSessionData.data.length
                      :
                      0 }}/{{ totalCount }})</span>
                    <span v-else>Readings - ({{ completedCount }}/{{ totalCount }})</span>
                    <span class="text-primary">{{ isOutlierActive ? `Showing results ${currentSession.outlier}% more or
                      less than the previous session.`: '' }}</span>
                  </h3>
                </div>
              </div>
            </div>

            <div class="table-responsive" v-if="currentReadingsRows.length">
              <!-- Projects table -->
              <b-table class="mt-3" striped hover
                :items="currentReadingsRows" :fields="fields"
                :tbody-tr-class="rowClass">
                <template slot="index" slot-scope="data">{{ data.index + 1 }}</template>
                <template slot="lot_id" slot-scope="data">{{ data.item.lot.lot_no }}</template>
                <template slot="ownerName" slot-scope="data">{{ data.item.lot && data.item.lot.lot_owner ? data.item.lot.lot_owner.name : '-' }}</template>
                <template slot="units_consumed" slot-scope="data">{{ data.value | formatNumber }}</template>
                <template slot="net_bill_value" slot-scope="data">{{ data.value | currency }}</template>
                <template slot="survey_start_date" slot-scope="data">{{ data.item.survey_start_date | fmtDateTime }}</template>
                <template slot="actions" slot-scope="data">
                  <b-link @click="detailView(data.item)" class="text-info">
                    <i class="far fa-eye mr-2"></i>
                  </b-link>
                </template>
              </b-table>
            </div>
            <div class="text-center my-5" v-else-if="!loaderMount">
              <h4>No Data Available</h4>
            </div>
            <div class="card-footer border-0" v-if="currentReadingsRows.length">
              <b-pagination v-if="isOutlierActive" class="float-right" @change="paginateOutlier"
                :per-page="outlierSessionData.data ? 20 : 1"
                :total-rows="outlierSessionData.data ? outlierSessionData.total : 0" v-model="currentPage" />
              <b-pagination v-else class="float-right" @change="pageClick" :per-page="selectedSessionData.per_page"
                :total-rows="selectedSessionData.total" v-model="currentPage" />

            </div>
          </div>

        

        </div>
      </div>
      <div class="row my-4" v-if="session == 0 && !viewDetailsSession">
        <div class="col-md-12 mb-5 mb-xl-0">
          <div class="card shadow mb-5">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">Create New Session</h3>
                </div>
              </div>
            </div>
            <div class="px-4 pb-4">

              <!-- Step 1: Site & Meter Type -->
              <div class="upload-step">
                <div class="upload-step-label">
                  <span class="step-number" :class="form.site_id && form.service_type_id ? 'step-done' : 'step-active'">1</span>
                  <span class="step-title">Select Site &amp; Meter Type</span>
                </div>
                <div class="row mt-3">
                  <div class="col-md-4">
                    <b-form-group label="Site" label-for="uploadSite">
                      <b-form-select id="uploadSite" @input="getMeterList()" v-model="form.site_id"
                        class="form-control-alternative">
                        <option value="null" disabled selected>Please select Site</option>
                        <option v-for="list in coPropList.data" :key="list.id" :value="list.id">{{ list.name }}</option>
                      </b-form-select>
                    </b-form-group>
                  </div>
                  <div class="col-md-4">
                    <b-form-group label="Meter Type" label-for="uploadMeter">
                      <b-form-select id="uploadMeter" v-model="form.service_type_id" :disabled="form.site_id == null"
                        class="form-control-alternative">
                        <option value="null" disabled selected>Please select Meter</option>
                        <option v-for="list in meterList" :key="list.id" :value="list.id">{{ list.service_name }}</option>
                      </b-form-select>
                    </b-form-group>
                  </div>
                </div>
              </div>

              <!-- Step 2: Download Template (unlocks after step 1) -->
              <div class="upload-step" :class="{ 'step-locked': !form.site_id || !form.service_type_id }">
                <div class="upload-step-label">
                  <span class="step-number" :class="form.site_id && form.service_type_id ? 'step-active' : ''">2</span>
                  <span class="step-title">Download Template</span>
                </div>
                <div class="mt-3">
                  <b-button variant="outline-info" @click="downloadTemplate()"
                    :disabled="!form.site_id || !form.service_type_id">
                    <i class="fas fa-download mr-2"></i> Download CSV Template
                  </b-button>
                  <small class="text-muted ml-3">Fill in the downloaded template, then upload below.</small>
                </div>
              </div>

              <!-- Step 3: Reading Date + Upload (unlocks after step 1) -->
              <div class="upload-step" :class="{ 'step-locked': !form.site_id || !form.service_type_id }">
                <div class="upload-step-label">
                  <span class="step-number" :class="form.site_id && form.service_type_id ? 'step-active' : ''">3</span>
                  <span class="step-title">Upload Data</span>
                </div>
                <div class="row mt-3 align-items-end">
                  <div class="col-md-3">
                    <b-form-group label="Reading Date" label-for="readingDate">
                      <b-form-input id="readingDate" type="date" v-model="form.reading_date"
                        :max="todayDate"
                        :disabled="!form.site_id || !form.service_type_id"
                        class="form-control-alternative"></b-form-input>
                    </b-form-group>
                  </div>
                  <div class="col-md-5">
                    <b-form-group label="CSV File" label-for="uploadFile">
                      <b-form-file id="uploadFile" accept=".csv" v-model="form.file"
                        :disabled="!form.reading_date || !form.site_id || !form.service_type_id"></b-form-file>
                    </b-form-group>
                  </div>
                  <div class="col-md-4 d-flex align-items-center pb-3">
                    <b-button @click="fileImport(form)" variant="success"
                      :disabled="!form.site_id || !form.service_type_id || !form.file || !form.reading_date">
                      <i class="fas fa-upload mr-2"></i> Upload
                    </b-button>
                    <b-link class="text-danger ml-3" @click="cancelUploadSession">Cancel</b-link>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      <div class="row my-4" v-if="session == null">
        <div class="col-md-12 mb-5 mb-xl-0">
          <div class="card shadow mb-5">
            <div class="row m-0 px-2 pb-4">
              <div class="col-md-12">
                <h4 class="py-5 my-3 text-center">Please select sessions</h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row my-4" v-if="viewDetailsSession">
        <div class="col-md-12 mb-5 mb-xl-0">
          <div class="card shadow mb-5 p-4">
            <div class="card-header border-0 bg-transparent">
              <h3 class="mb-0"><i class="fas fa-eye text-primary mr-2"></i> View Details Session</h3>
            </div>

            <!-- INFO ROW -->
            <div v-if="individualDetails.suspicion_reason && individualDetails.suspicion_reason !== 'none'"
              class="mt-3">
              <p class="text-danger font-weight-bold">{{ individualDetails.suspicion_reason }}</p>
            </div>
            <div class="row mb-4 summary-box">
              <div class="col-md-4">
                <p class="mb-1 text-muted font-weight-bold">Lot No.</p>
                <p>{{ individualDetails.lot.lot_no }}</p>

                <p class="mb-1 text-muted font-weight-bold">Previous Unit</p>
                <p>{{ individualDetails.previous_unit || 0 | formatNumber }}</p>
              </div>

              <div class="col-md-4">
                <p class="mb-1 text-muted font-weight-bold">Owner</p>
                <p>{{ individualDetails.lot && individualDetails.lot.lot_owner ? individualDetails.lot.lot_owner.name : '-' }}</p>

                <p class="mb-1 text-muted font-weight-bold">Current Unit</p>
                <div v-if="!editCurrentUnit">
                  <p>
                    {{ individualDetails.current_unit | formatNumber }}
                    <b-btn size="sm" variant="primary"
                      @click="editCurrentUnitValue = individualDetails.current_unit; editCurrentUnit = true">
                      <i class="fas fa-edit"></i>
                    </b-btn>
                  </p>
                </div>
                <div v-if="editCurrentUnit">
                  <b-form-input type="number" v-model="editCurrentUnitValue" autofocus />
                  <div class="mt-2">
                    <b-btn size="sm" variant="warning" @click="editCurrentUnit = false">Cancel</b-btn>
                    <b-btn size="sm" variant="success" @click="saveCurrentUnit">Save</b-btn>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <p class="mb-1 text-muted font-weight-bold">Barcode</p>
                <p>{{ individualDetails.barcode ? individualDetails.barcode.barcode : '-' }}</p>

                <p class="mb-1 text-muted font-weight-bold">Meter ID</p>
                <p>{{ individualDetails.barcode ? individualDetails.barcode.meter_id : '-' }}</p>
              </div>
            </div>
            <!-- METER IMAGE -->
            <div class="row mb-4">
              <div class="col text-center">
                <b-btn variant="info" @click="$refs.imgModal.show()">
                  <i class="fas fa-image mr-2"></i>View Image
                </b-btn>
              </div>
            </div>

            <div class="text-center">
              <b-btn variant="primary" @click="viewDetailsSession = false">Close</b-btn>
            </div>
          </div>
        </div>
      </div>

      <!----->

      <b-modal ref="imgModal" size="xl" hide-footer :title="'Lot No: ' + currentLotNo"
        :dialog-class="'fullscreen-modal'" centered no-close-on-backdrop no-close-on-esc @hidden="hideLoupe">
        <div class="image-viewer-container">
          <div v-if="loadingImage" class="spinner-border text-primary loader-center" role="status">
            <span class="sr-only">Loading Image...</span>
          </div>
          <template v-else>
            <b-button variant="light" class="download-btn" @click="downloadImage" v-b-tooltip.hover
              title="Download Image">
              <i class="fas fa-download"></i>
            </b-button>
            <div class="loupe-wrapper fullscreen-image-wrapper" @mouseenter="showLoupe" @mouseleave="hideLoupe"
              @mousemove="updateLoupe">
              <img ref="meterImage" class="fullscreen-image" :src="signedImgUrl || noImageUrl" @load="handleMeterImageLoad"
                :alt="signedImgUrl ? 'Meter image' : 'No image'" />
              <div v-if="loupeVisible" class="loupe-lens" :style="loupeLensStyle"></div>
            </div>
          </template>
        </div>
      </b-modal>

    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { apiUrl, storagePath } from "@/config";
import noImageUrl from "@/assets/img/theme/noImage.png";
import userData from "@/pages/Common/userData";
import { getPreferences, unitLabel } from "@/utils/preferences";
export default {
  components: { userData },
  data: () => ({
    editCurrentUnit: false,
    showSuspicionCollapse: false,
    editCurrentUnitValue: "",
    showCollapse: false,
    signedImgUrl: '',
    loadingImage: false,
    currentLotNo: '',
    imgLoaded: false,
    loupeVisible: false,
    loupeLensStyle: {},
    pagination: {
      total: null
    },
    totalRecords: null,
    isOutlierActive: false,
    loaderMount: false,
    currentSession: {
      units_consumed_by_building: "",
      load_consumed_by_building: "",
      tanker_cost: "",
      tanker_volume: "",
      outlier: null
    },
    reviewedLots: [],
    ignoreWarnings: false,
    warningsAcknowledged: false,
    totalPageCount: 1,
    noImageUrl: noImageUrl,
    currentPage: 1,
    uploadFile: null,
    loading: false,
    totalCount: 0,
    completedCount: 0,
    todayDate: new Date().toISOString().split("T")[0],
    fileUpload: false,
    storagePath: storagePath,
    apiUrl: apiUrl,
    individualDetails: {},
    form: {
      site_id: null,
      service_type_id: null,
      file: "",
      reading_date: null
    },
    error: {
      percentage: false
    },
    viewDetailsSession: false,
    session: null,
    survoyor: null,
    startedOn: "",
    mostRecentClosedSession: null,
    openSessions: [],
    completedSessions: [],
    skipRouteSessionRestore: false,
    refreshing: false,
    fields: [
      "index",
      {
        key: "lot_id",
        label: "Lot No",
        sortable: true,
        class: "text-center"
      },
      {
        key: "ownerName",
        label: "Owner Name",
        sortable: true,
        class: "text-center"
      },
      {
        key: "survey_start_date",
        label: "Reading Date",
        sortable: true,
        class: "text-center"
      },
      {
        key: "units_consumed",
        label: "Units Consumed",
        sortable: true,
        class: "text-center"
      },
      {
        key: "net_bill_value",
        label: "Billable Total",
        sortable: true,
        class: "text-center"
      },
      {
        key: "actions",
        label: "Actions",
        sortable: false,
        class: "text-center"
      }
    ]
  }),
  computed: {
    ...mapGetters("coprops", ["coPropList", "meterList"]),
    ...mapGetters("sessions", [
      "openSessionList",
      "selectedSessionData",
      "totalBillableUnits",
      "outlierSessionData",
      "incompleteData",
      "suspiciousMessages",
      "completedSessionData"
    ]),
    suspiciousLots() {
      // console.log(this.suspiciousMessages, "xxxsuspiciousMessages");
      return this.suspiciousMessages || [];
    },
    allReviewed() {
      return this.ignoreWarnings || this.warningsAcknowledged || this.suspiciousLots.every(message => this.reviewedLots.includes(message));
    },
    isWaterSession() {
      return this.currentSession.service_type_id === 1;
    },
    prefs() {
      return getPreferences()
    },
    currencySymbol() {
      return this.prefs.currency || 'Rs.'
    },
    waterUnitLabel() {
      return unitLabel(1, this.prefs)
    },
    electricityUnitLabel() {
      return unitLabel(2, this.prefs)
    },
    currentSessionUnitLabel() {
      return unitLabel(this.currentSession.service_type_id, this.prefs)
    },
    getData() {
      // console.log(this.selectedSessionData, "===selectedSessionData");
      // console.log(this.incompleteData, "===incompleteData");
    },
    selectedSessionRows() {
      return this.selectedSessionData && Array.isArray(this.selectedSessionData.data)
        ? this.selectedSessionData.data
        : [];
    },
    currentReadingsRows() {
      return this.isOutlierActive
        ? (this.outlierSessionData && Array.isArray(this.outlierSessionData.data) ? this.outlierSessionData.data : [])
        : this.selectedSessionRows;
    },
    selectedSessionFirstRow() {
      return this.selectedSessionRows.length ? this.selectedSessionRows[0] : null;
    },
    currentSiteName() {
      if (!this.currentSession.site_id || !this.coPropList.data) return '';
      const site = this.coPropList.data.find(s => s.id == this.currentSession.site_id);
      return site ? site.name : '';
    },
    currentServiceTypeName() {
      if (!this.currentSession.service_type_id || !this.openSessionList) return '';
      const session = this.openSessionList.find(s => s.service_type_id == this.currentSession.service_type_id && s.site_id == this.currentSession.site_id);
      return session ? session.service_type.service_name : '';
    }
  },
  methods: {
    ...mapMutations("sessions", ["clearActiveSessionState"]),
    ...mapActions("coprops", ["ALL_CO_PROP_LIST", "ALL_METER_LIST"]),
    ...mapActions("sessions", [
      "OPEN_SESSION_LIST",
      "SELECTED_SESSION_LIST",
      "SESSION_UPLOAD",
      "CLOSED_SESSION",
      "DISCARD_SESSION",
      "SUPPORT_BOWSER_ALLOCATION",
      "UPDATE_CURRENT_UNIT",
      "AUTO_COMPLETED_MISSING_LOTS",
      "OUTLIER_SESSION_LIST",
      "COMPLETED_SESSION_LIST",
      "COMPLETED_SESSION_MAIL"
    ]),

    createDefaultCurrentSession() {
      return {
        units_consumed_by_building: "",
        load_consumed_by_building: "",
        tanker_cost: "",
        tanker_volume: "",
        outlier: null
      };
    },

    resetSelectedSessionState() {
      this.clearActiveSessionState();
      this.session = null;
      this.viewDetailsSession = false;
      this.individualDetails = {};
      this.survoyor = null;
      this.startedOn = "";
      this.mostRecentClosedSession = null;
      this.totalCount = 0;
      this.completedCount = 0;
      this.currentPage = 1;
      this.totalRecords = null;
      this.pagination.total = null;
      this.isOutlierActive = false;
      this.loaderMount = false;
      this.reviewedLots = [];
      this.ignoreWarnings = false;
      this.warningsAcknowledged = false;
      this.currentSession = this.createDefaultCurrentSession();
      this.currentLotNo = "";
      this.signedImgUrl = "";
      this.loadingImage = false;
      this.imgLoaded = false;
      this.loupeVisible = false;
      this.loupeLensStyle = {};
    },

    rowClass(item, type) {
      if (!item || type !== 'row') return '';
      return item.suspicion_reason && item.suspicion_reason !== 'none' ? 'table-danger' : '';
    },

    downloadImage() {
      const link = document.createElement('a');
      link.href = this.signedImgUrl || this.noImageUrl;
      link.download = 'meter_image.jpg';
      link.click();
    },
    handleMeterImageLoad() {
      this.imgLoaded = true;
      this.hideLoupe();
    },
    showLoupe() {
      if (!this.imgLoaded) return;
      this.loupeVisible = true;
    },
    hideLoupe() {
      this.loupeVisible = false;
    },
    updateLoupe(event) {
      const image = event.currentTarget.querySelector("img");
      if (!image || !this.imgLoaded) return;

      const rect = image.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;

      if (offsetX < 0 || offsetY < 0 || offsetX > rect.width || offsetY > rect.height) {
        this.hideLoupe();
        return;
      }

      const lensSize = 180;
      const zoom = 2.5;
      const naturalWidth = image.naturalWidth || rect.width;
      const naturalHeight = image.naturalHeight || rect.height;
      const backgroundX = (offsetX / rect.width) * naturalWidth * zoom;
      const backgroundY = (offsetY / rect.height) * naturalHeight * zoom;

      this.loupeVisible = true;
      this.loupeLensStyle = {
        width: `${lensSize}px`,
        height: `${lensSize}px`,
        left: `${offsetX - lensSize / 2}px`,
        top: `${offsetY - lensSize / 2}px`,
        backgroundImage: `url("${image.currentSrc || image.src}")`,
        backgroundRepeat: "no-repeat",
        backgroundSize: `${naturalWidth * zoom}px ${naturalHeight * zoom}px`,
        backgroundPosition: `-${backgroundX - lensSize / 2}px -${backgroundY - lensSize / 2}px`
      };
    },
    onIgnoreWarningsToggle(isEnabled) {
      if (isEnabled) {
        this.$refs.ignoreWarningsModal.show();
      }
    },
    cancelIgnoreWarnings() {
      this.ignoreWarnings = false;
      this.$refs.ignoreWarningsModal.hide();
    },
    confirmIgnoreWarnings() {
      this.ignoreWarnings = true;
      this.$refs.ignoreWarningsModal.hide();
    },
    acknowledgeWarnings() {
      this.warningsAcknowledged = true;
      this.SUCCESS_TOAST_MSG(`${this.suspiciousLots.length} warning(s) acknowledged`);
    },

    saveCurrentUnit() {
      if (this.individualDetails.previous_unit > this.editCurrentUnitValue) {
        this.ERROR_TOAST_MSG("Please enter value greater than previous unit ");
        return;
      }

      var obj = {
        id: this.individualDetails.open_session_id,
        current_reading_unit: this.editCurrentUnitValue
      };
      this.UPDATE_CURRENT_UNIT(obj)
        .then(() => {
          this.individualDetails.current_unit = this.editCurrentUnitValue;
          this.editCurrentUnit = false;
          if (this.isOutlierActive) {
            this.outlierSession();
          } else {
            this.pageClick();
          }
          this.SUCCESS_TOAST_MSG("Current reading updated successfully");
        })
        .catch(err => {
          this.ERROR_TOAST_MSG("Current reading not updated");
        });
    },

    refreshSessions() {
      this.refreshing = true;
      this.getOpenSessionList().finally(() => {
        this.refreshing = false;
      });
    },

    getOpenSessionList() {
      this.completedSessions = [];
      this.openSessions = [];
      return this.OPEN_SESSION_LIST()
        .then(() => {
          for (let i = 0; i < this.openSessionList.length; i++) {
            const item = this.openSessionList[i];
            if (!item.site) continue;
            const text = item.site.name + " [" + item.service_type.service_name + "]";
            const entry = {
              total_count: item.total_lot_count,
              completed_count: item.completed_lot_count,
              site_id: item.site_id,
              service_type_id: item.service_type_id,
              text
            };
            if (item.completed_lot_count == item.total_lot_count) {
              this.completedSessions.push(entry);
            } else {
              this.openSessions.push(entry);
            }
          }
          if (this.skipRouteSessionRestore) {
            this.skipRouteSessionRestore = false;
          } else if (this.$route.query.new === '1') {
            this.session = '0';
          } else if (this.$route.query.type) {
            let sessionFromRouteQuery = atob(this.$route.query.type);
            this.session = sessionFromRouteQuery;
            this.selectedSession(sessionFromRouteQuery);
          }
        })
        .catch(() => { });
    },

    async downloadTemplate() {
      try {
        const token = localStorage.getItem('token');
        const url = `${apiUrl}csv?site_id=${this.form.site_id}&service_type_id=${this.form.service_type_id}`;
        const res = await fetch(url, { headers: { 'Authorization': token } });
        if (!res.ok) {
          const err = await res.json();
          throw err;
        }
        // Content-Disposition is only readable if the server sends
        // Access-Control-Expose-Headers: Content-Disposition.
        // Fall back to a client-side filename using the selected site name.
        const disposition = res.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename=(.+)/);
        let filename;
        if (match) {
          filename = match[1].trim();
        } else {
          const site = this.coPropList.data && this.coPropList.data.find(s => s.id == this.form.site_id);
          const siteName = site ? site.name : 'Site';
          const date = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '');
          filename = `Upload session ${siteName} ${date}.csv`;
        }
        const blob = await res.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (err) {
        this.ERROR_TOAST_MSG((err && err.message) ? err.message : 'Failed to download template');
      }
    },

    uploadSession() {
      this.fileUpload = !this.fileUpload;
      // Get all site list
      this.ALL_CO_PROP_LIST()
        .then(() => { })
        .catch(() => { });
      // Get all Meter list
      // this.ALL_METER_LIST().then(()=>{
      // }).catch(() => {
      // })
    },

    checkElectricity() {
      if (this.currentSession.service_type_id === 2) {
        this.$refs.closeSessionElectricModal.show();
      } else if (this.currentSession.service_type_id === 1) {
        this.$refs.closeSessionWaterModal.show();
      } else {
        this.$refs.closeSessionModal.show();
      }
    },
    saveWaterDetails() {
      const cost = this.currentSession.tanker_cost;
      const volume = this.currentSession.tanker_volume;
      const hasCost = cost !== '' && cost !== null && cost !== undefined;
      const hasVolume = volume !== '' && volume !== null && volume !== undefined;
      if (hasCost !== hasVolume) {
        this.ERROR_TOAST_MSG('Both Tanker Cost and Tanker Volume are required, or skip if not applicable.');
        return;
      }
      if (hasCost) {
        const costNum = Number(cost);
        const volumeNum = Number(volume);
        if (Number.isNaN(costNum) || costNum <= 0) {
          this.ERROR_TOAST_MSG('Tanker Cost must be greater than 0');
          return;
        }
        if (Number.isNaN(volumeNum) || volumeNum <= 0) {
          this.ERROR_TOAST_MSG('Tanker Volume must be greater than 0');
          return;
        }
      }
      this.$refs.closeSessionWaterModal.hide();
      this.$refs.closeSessionModal.show();
    },
    skipWaterDetails() {
      this.currentSession.tanker_cost = '';
      this.currentSession.tanker_volume = '';
      this.$refs.closeSessionWaterModal.hide();
      this.$refs.closeSessionModal.show();
    },
    cancelWaterDetails() {
      this.currentSession.tanker_cost = '';
      this.currentSession.tanker_volume = '';
      this.$refs.closeSessionWaterModal.hide();
    },

    autoCompleteLotsModal() {
      this.$refs.autoCompleteLotsModal.show();
    },

    autoCompleteMissingLots(session) {
      this.$refs.autoCompleteLotsModal.hide();
      const selectedSessionFirstRow = this.selectedSessionFirstRow;
      const incomplteLots = this.incompleteData.map(oo => oo.lot_no);
      const siteId = Number(session.split("-")[0]);
      const serviceTypeId = Number(session.split("-")[1]);
      const payload = {
        uuid: selectedSessionFirstRow ? selectedSessionFirstRow.uuid : null,
        incomplteLots: incomplteLots,
        site_id: siteId,
        service_type_id: serviceTypeId
      };
      this.AUTO_COMPLETED_MISSING_LOTS(payload)
        .then(() => {
          document.body.classList.remove('modal-open');
          this.skipRouteSessionRestore = true;
          this.getOpenSessionList().then(() => {
            const allSessions = [...this.openSessions, ...this.completedSessions];
            const updated = allSessions.find(s => s.site_id == siteId && s.service_type_id == serviceTypeId);
            if (updated) {
              const sessionValue = `${updated.site_id}-${updated.service_type_id}-${updated.total_count}-${updated.completed_count}`;
              this.session = sessionValue;
              this.selectedSession(sessionValue);
            } else {
              this.selectedSession(this.session);
            }
          });
        })
        .catch(() => {
          this.ERROR_TOAST_MSG("Auto complete failed.");
        });
    },

    getMeterList() {
      this.form.service_type_id = null;
      if (!this.form.site_id || this.form.site_id === "null") {
        return Promise.resolve();
      }
      let meterListDetails = {
        site_id: this.form.site_id
      };
      this.ALL_METER_LIST(meterListDetails)
        .then(() => { })
        .catch(() => { });
    },

    exportSampleSheet() {
      process.env.BASE_URL + "";
    },

    selectedSession(data) {
      let value = data.split("-");
      this.$router.replace({
        name: "openSession",
        query: { type: btoa(data) }
      });
      this.currentSession = {
        site_id: Number(value[0]),
        service_type_id: Number(value[1]),
        units_consumed_by_building: "",
        load_consumed_by_building: "",
        tanker_cost: "",
        tanker_volume: "",
        outlier: null,
        page: 1
      };
      // Reset warning acknowledgement when switching sessions
      this.warningsAcknowledged = false;
      this.reviewedLots = [];
      this.ignoreWarnings = false;

      if (value[0] != 0) {
        this.loaderMount = true;

        this.SELECTED_SESSION_LIST(this.currentSession)
          .then(() => {
            const selectedSessionFirstRow = this.selectedSessionFirstRow;
            this.totalCount = value[2];
            this.completedCount = value[3];
            this.survoyor = selectedSessionFirstRow && selectedSessionFirstRow.surveyor
              ? selectedSessionFirstRow.surveyor
              : null;
            this.pagination.total = this.selectedSessionData.total;
            this.totalRecords = this.selectedSessionData.total;
            this.loaderMount = false;
          })
          .catch(() => {
            this.loaderMount = false;
          });
      }
    },

    cancelUploadSession() {
      this.fileUpload = !this.fileUpload;
      this.form = {
        site_id: null,
        service_type_id: null,
        file: "",
        reading_date: null
      };
    },
    fileImport(data) {
      this.loading = true;
      if (!data.site_id) {
        this.loading = false;
        this.ERROR_TOAST_MSG("Please select coprops");
      } else if (!data.service_type_id) {
        this.loading = false;
        this.ERROR_TOAST_MSG("Please select meter type");
      } else if (!data.reading_date) {
        this.loading = false;
        this.ERROR_TOAST_MSG("Please select reading date");
      } else if (!data.file) {
        this.loading = false;
        this.ERROR_TOAST_MSG("Please upload a file");
      } else {
        const uploadedSiteId = data.site_id;
        const uploadedServiceTypeId = data.service_type_id;
        let formData = new FormData();
        formData.append("site_id", data.site_id);
        formData.append("service_type_id", data.service_type_id);
        formData.append("file", data.file);
        formData.append("reading_date", data.reading_date);
        this.SESSION_UPLOAD(formData)
          .then(() => {
            this.loading = false;
            this.cancelUploadSession();
            this.OPEN_SESSION_LIST().then(() => {
              for (let i = 0; i < this.openSessionList.length; i++) {
                const s = this.openSessionList[i];
                if (s.site_id == uploadedSiteId && s.service_type_id == uploadedServiceTypeId) {
                  const sessionValue = `${s.site_id}-${s.service_type_id}-${s.total_lot_count}-${s.completed_lot_count}`;
                  this.session = sessionValue;
                  this.selectedSession(sessionValue);
                  break;
                }
              }
              this.SUCCESS_TOAST_MSG("Session uploaded successfully");
            });
          })
          .catch(err => {
            this.loading = false;
            this.ERROR_TOAST_MSG((err && (err.message || err.error)) ? (err.message || err.error) : "Upload failed. Please check your file and try again.");
          });
      }
    },
    pageClick(data) {
      // console.log('this.currentSession',this.currentSession)
      // return
      this.loaderMount = true;
      if (data) this.currentPage = data;
      this.currentSession.page = this.currentPage;

      this.SELECTED_SESSION_LIST(this.currentSession)
        .then(() => {
          const selectedSessionFirstRow = this.selectedSessionFirstRow;
          // this.totalCount = value[2];
          // this.completedCount = value[3];
          this.survoyor = selectedSessionFirstRow && selectedSessionFirstRow.surveyor
            ? selectedSessionFirstRow.surveyor
            : null;

          this.pagination.total = this.selectedSessionData.total;
          this.loaderMount = false;
        })
        .catch(() => {
          this.loaderMount = false;
        });
    },
    closedSession(data) {
      const payload = {
        site_id: data.site_id,
        service_type_id: data.service_type_id,
        units_consumed_by_building: data.units_consumed_by_building,
        load_consumed_by_building: data.load_consumed_by_building
      };
      if (this.isWaterSession) {
        const cost = data.tanker_cost;
        const volume = data.tanker_volume;
        const hasCost = cost !== '' && cost !== null && cost !== undefined;
        const hasVolume = volume !== '' && volume !== null && volume !== undefined;
        if (hasCost && hasVolume) {
          payload.tanker_cost = Number(cost);
          payload.tanker_volume = Number(volume);
        }
      }
      this.CLOSED_SESSION(payload)
        .then(() => {
          this.$refs.closeSessionModal.hide();
          this.$refs.closeSessionElectricModal.hide();
          this.currentSession.units_consumed_by_building = "";
          this.currentSession.load_consumed_by_building = "";
          this.currentSession.tanker_cost = "";
          this.currentSession.tanker_volume = "";

          // Fetch completed sessions and show post-close modal
          this.COMPLETED_SESSION_LIST({
            site_id: data.site_id,
            service_type_id: data.service_type_id,
            page: 1
          }).then(() => {
            const sessions = this.completedSessionData &&
              this.completedSessionData.data &&
              this.completedSessionData.data.data
                ? this.completedSessionData.data.data
                : [];
            if (sessions.length > 0) {
              this.mostRecentClosedSession = sessions[0];
              this.$refs.postCloseModal.show();
            } else {
              this.$router.push({ name: "completedSession" });
            }
          }).catch(() => {
            this.$router.push({ name: "completedSession" });
          });

          this.session = null;
          this.getOpenSessionList();
        })
        .catch(() => {
          // Global interceptor already surfaces the backend message as a toast.
          // Modal stays open so the user can correct the bowser amount and retry.
        });
    },
    dismissPostCloseModal() {
      this.$refs.postCloseModal.hide();
      document.body.classList.remove('modal-open');
      if (!this.mostRecentClosedSession) {
        this.$router.push({ name: "completedSession" });
        return;
      }
      const session = this.mostRecentClosedSession;
      this.$router.push({
        name: "completedSession",
        query: {
          prefill: btoa(JSON.stringify({
            site_id: (session.site && session.site.id) || session.site_id,
            service_type_id: session.service_type.id
          }))
        }
      });
    },
    actionViewReadings() {
      if (!this.mostRecentClosedSession) return;
      this.$refs.postCloseModal.hide();
      document.body.classList.remove('modal-open');
      const s = this.mostRecentClosedSession;
      this.$router.push({
        name: "completedSession",
        query: { view: btoa(JSON.stringify({ site_id: (s.site && s.site.id) || s.site_id, service_type_id: s.service_type.id, uuid: s.uuid, created_at: s.created_at })) }
      });
    },
    actionDownloadExcel() {
      if (!this.mostRecentClosedSession) return;
      const session = this.mostRecentClosedSession;
      const url = `${this.apiUrl}site-service-csv?site_id=${(session.site && session.site.id) || session.site_id}&service_type_id=${session.service_type.id}&created_at=${session.created_at}&sp=${session.service_provider_id}&uuid=${session.uuid}`;
      window.open(url, '_blank');
    },
    actionDownloadPDF() {
      if (!this.mostRecentClosedSession) return;
      const session = this.mostRecentClosedSession;
      const url = `${this.apiUrl}site-service-pdf?site_id=${(session.site && session.site.id) || session.site_id}&service_type_id=${session.service_type.id}&created_at=${session.created_at}&sp=${session.service_provider_id}&uuid=${session.uuid}`;
      window.open(url, '_blank');
    },
    actionDownloadNotify() {
      if (!this.mostRecentClosedSession) return;
      const session = this.mostRecentClosedSession;
      const url = `${this.apiUrl}site-service-pdf?site_id=${(session.site && session.site.id) || session.site_id}&service_type_id=${session.service_type.id}&created_at=${session.created_at}&sp=${session.service_provider_id}&uuid=${session.uuid}&view=notify`;
      window.open(url, '_blank');
    },
    actionDownloadText() {
      if (!this.mostRecentClosedSession) return;
      const session = this.mostRecentClosedSession;
      const url = `${this.apiUrl}site-service-txt?site_id=${(session.site && session.site.id) || session.site_id}&sp=${session.service_provider_id}&uuid=${session.uuid}`;
      window.open(url, '_blank');
    },
    actionEmailBill() {
      if (!this.mostRecentClosedSession) return;
      const session = this.mostRecentClosedSession;
      const payload = {
        site_id: (session.site && session.site.id) || session.site_id,
        uuid: session.uuid,
        service_type_id: session.service_type.id,
        created_at: session.created_at,
        type: 'bill'
      };
      this.COMPLETED_SESSION_MAIL(payload).then(() => {
        this.SUCCESS_TOAST_MSG('Bill email scheduled successfully');
        this.$refs.postCloseModal.hide();
        document.body.classList.remove('modal-open');
        this.$router.push({ name: "completedSession" });
      }).catch(() => {
        this.ERROR_TOAST_MSG('Failed to schedule bill email');
      });
    },
    actionEmailNotice() {
      if (!this.mostRecentClosedSession) return;
      const session = this.mostRecentClosedSession;
      const payload = {
        site_id: (session.site && session.site.id) || session.site_id,
        uuid: session.uuid,
        service_type_id: session.service_type.id,
        created_at: session.created_at,
        type: 'notify'
      };
      this.COMPLETED_SESSION_MAIL(payload).then(() => {
        this.SUCCESS_TOAST_MSG('Notice email scheduled successfully');
        this.$refs.postCloseModal.hide();
        document.body.classList.remove('modal-open');
        this.$router.push({ name: "completedSession" });
      }).catch(() => {
        this.ERROR_TOAST_MSG('Failed to schedule notice email');
      });
    },
    paginateOutlier(page) {
      this.currentPage = page;
      this.outlierSession();
    },
    outlierSession() {
      this.loaderMount = true;
      if (!this.isOutlierActive) this.currentPage = 1;
      this.isOutlierActive = true;

      this.currentSession.page = this.currentPage;
      this.$refs.outlierSessionModal.hide();
      this.OUTLIER_SESSION_LIST(this.currentSession)
        .then(() => {
          this.pagination.total = this.selectedSessionData.total;
          this.loaderMount = false;
        })
        .catch(() => {
          this.loaderMount = false;
        });
    },
    discardSession(data) {
      this.$refs.discardSessionModal.hide();
      this.DISCARD_SESSION(data)
        .then(() => {
          this.resetSelectedSessionState();
          this.skipRouteSessionRestore = true;
          if (this.$route.query && this.$route.query.type) {
            window.history.replaceState({}, document.title, this.$route.path);
          }
          this.getOpenSessionList();
        })
        .catch(() => { });
    },
    detailView(data) {
      this.viewDetailsSession = true;
      this.individualDetails = data;
      this.currentLotNo = data.lot && data.lot.lot_no ? data.lot.lot_no : '';
      this.signedImgUrl = '';
      this.loadingImage = !!data.meter_image;
      this.imgLoaded = false;
      this.hideLoupe();

      if (data.meter_image) {
        const encodedPath = btoa(data.meter_image);
        fetch(`${this.apiUrl}signed-url/${encodedPath}`, {
          headers: {
            Authorization: `Bearer ${localStorage.token}`,
          },
        })
          .then((res) => res.json())
          .then((res) => {
            if (res.status && res.url) {
              this.signedImgUrl = res.url;
            }
          })
          .catch(() => {
            this.signedImgUrl = '';
          })
          .finally(() => {
            this.loadingImage = false;
          });
      } else {
        this.loadingImage = false;
      }
    },
    checkOutlierPercentage(val) {
      let percentage = parseInt(val) || 0;
      if (percentage < 0) {
        this.currentSession.outlier = 0;
      } else if (percentage > 100) {
        this.currentSession.outlier = 100;
      } else {
        this.currentSession.outlier = Number(val);
      }
    },
    showOutlier() {
      if (!this.currentSession.outlier) this.currentSession.outlier = 15;
      this.$refs.outlierSessionModal.show();
    }
  },
  created() {
    // Clean up any stale modal-open class that may block page scrolling
    document.body.classList.remove('modal-open');

    if (!localStorage.token) {
      localStorage.clear();
      next({
        name: "adminLogin"
      });
    } else {
      if (localStorage.roleId == 1) {
        this.$router.replace({ name: "dashboard" });
      }
      this.getOpenSessionList();
      this.ALL_CO_PROP_LIST().then(() => {}).catch(() => {});
    }
  },
  beforeDestroy() {
    document.body.classList.remove('modal-open');
  }
};
</script>
<style>
.sessionExport:hover a {
  color: #fff !important;
}

.c-pointer {
  cursor: pointer;
}

/* Session summary bar — matches completedSession style */
.session-summary-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0;
  border-radius: 10px;
  background: #f8fafd;
  border: 1px solid #dbeafe;
}

.stat-chip {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 12px 20px;
  border-right: 1px solid #e2eaf5;
}

.stat-chip:last-child {
  border-right: none;
}

.stat-chip--selector {
  width: 260px;
  flex-shrink: 0;
}

.stat-chip--selector .form-control {
  width: 220px;
}

.stat-chip--actions {
  flex-direction: row;
  align-items: center;
  gap: 0;
  padding: 12px 16px;
}

.stat-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-bottom: 3px;
  white-space: nowrap;
}

.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

/* Upload session step flow */
.upload-step {
  padding: 20px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: opacity 0.2s ease;
}
.upload-step:last-child {
  border-bottom: none;
}
.upload-step.step-locked {
  opacity: 0.4;
  pointer-events: none;
}
.upload-step-label {
  display: flex;
  align-items: center;
  gap: 12px;
}
.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  background: #e9ecef;
  color: #adb5bd;
}
.step-number.step-active {
  background: #5e72e4;
  color: #fff;
}
.step-number.step-done {
  background: #2dce89;
  color: #fff;
}
.step-title {
  font-weight: 600;
  font-size: 14px;
  color: #32325d;
}
.pointer-events-none {
  pointer-events: none;
}

.text-color-brand-green {
  color: #73c761;
}

.missing-lots-table th,
.missing-lots-table td {
  vertical-align: middle;
}

.barcode-pill {
  min-width: 110px;
  padding: 0.5rem 0.85rem;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
}

.loupe-lens {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/pages/Admin/openSession.vue